<template>
    <FormLayout>
        <FormLayoutRow>
            <FormLayoutLabel>{{ $tm('general.flagtimeoptional') }}</FormLayoutLabel>
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel>{{ $tm('general.flag') }}</FormLayoutLabel>
            <DropdownComp
                :dropdown-items="dropdownItems"
                :model-value="selectedExitStatus"
                :translationkey="`exitstatus.${selectedExitStatus}`"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel>{{ $tm('general.flagtime') }}</FormLayoutLabel>
            <InputNullableDateTime
                v-model="flagTime"
                format="DATETIMEMS3"
                :prefill-date="prefillDate ?? new Date()"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel>{{ $tm('general.dnxreason') }}</FormLayoutLabel>
            <InputText v-model="dnxReason" />
        </FormLayoutRow>
    </FormLayout>
</template>
<script setup lang="ts">
import { DropdownComp, InputNullableDateTime, InputText } from '@/components';
import { ref } from 'vue';
import { ExitStatusValues, SetRaceContestantExitStatusRequest, type ExitStatus } from '@/objects/generated';
import { DropdownItemButton } from '@/objects';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { RaceContestantDao } from '@/dao';
import { compareDateTimesNullable } from '@/utilities/dateutils';

const props = defineProps<{
    /**
     * The current exitStatus
     */
    exitStatus: ExitStatus;

    /**
     * When the exitStatus was set
     */
    exitStatusFlagTime: Date | null;

    /**
     * The user supplied reason why the exit status was set
     */
    exitStatusReason: string | null;

    /**
     * The raceContestant we want to update
     */
    raceContestantId: number;

    /**
     * The event we are currently in
     */
    eventId: number;

    /**
     * Prefilled flagtime
     * This is the timestamp that will be in the flagtime box to begin with
     */
    prefillFlagTime?: Date | null;

    /**
     * Prefill date for flagtime
     * Only applicable if we dont have a prefillFlagTime
     * Sets the date part of the prefillFlagTime
     * Typically event date
     */
    prefillDate?: Date;
}>();

const selectedExitStatus = ref<ExitStatus>(props.exitStatus);

const flagTime = ref<Date | null>(props.exitStatusFlagTime ?? props.prefillFlagTime ?? null);

const dnxReason = ref<string | null>(props.exitStatusReason);

const dropdownItems = ExitStatusValues.map((value) =>
    DropdownItemButton.create({
        action: () => {
            selectedExitStatus.value = value;
        },
        translationkey: `exitstatus.${value}`,
    }),
);

const save = async () => {
    const request = SetRaceContestantExitStatusRequest.FromJsValues({
        ExitStatus: selectedExitStatus.value,
        RaceContestantIds: [props.raceContestantId],
        FlagTime: flagTime.value,
        Reason: dnxReason.value == '' ? undefined : dnxReason.value,
    });

    await RaceContestantDao.SetRaceContestantsExitStatus(props.eventId, request);
};

const saveButtonEnabled = () => {
    if (selectedExitStatus.value !== props.exitStatus) {
        return true;
    }

    // Return early if we have "None", since we only care about the flagtime if we have an actual exit status
    if (selectedExitStatus.value === 'None') {
        return false;
    }

    if (compareDateTimesNullable(flagTime.value, props.exitStatusFlagTime) !== 0) {
        return true;
    }

    return false;
};

defineExpose({
    save,
    saveButtonEnabled,
});
</script>
